import { DataConnection, DataGlueGraph, NodeInstance } from '@glueapp/graph-types';
import { ValidationError, ValidationRule, ValidationRuleResult } from '../ValidationRule';
import { DataType, getStringRepresentationFromType, typeAMatchesTypeB, unwrapAllResolvedGenerics } from '@glueapp/graph-types';

export const TypeCompatibilityRule: ValidationRule = (graph: DataGlueGraph): ValidationRuleResult => {
    const errors: ValidationError[] = [];

    const mainGraphErrors = validateConnectionTypes(graph.connections, graph.nodeInstances, 'main');
    errors.push(...mainGraphErrors);

    for (const func of graph.functions) {
        const functionErrors = validateConnectionTypes(func.connections, func.nodeInstances, func.id);
        errors.push(...functionErrors);
    }

    return {
        valid: errors.length === 0,
        errors,
    };
};

function validateConnectionTypes(connections: DataConnection[], nodeInstances: NodeInstance[], context: string): ValidationError[] {
    const errors: ValidationError[] = [];
    const nodeMap = new Map(nodeInstances.map(node => [node.id, node]));

    for (const connection of connections) {
        // Skip inherent connections - they don't have meaningful types to check
        if (connection.sendingPortType === 'inherent' || connection.receivingPortType === 'inherent') {
            continue;
        }

        const sourceNode = nodeMap.get(connection.sendingNodeInstanceId);
        const targetNode = nodeMap.get(connection.receivingNodeInstanceId);

        // Skip if nodes don't exist (this would be caught by ConnectionIntegrityRule)
        if (!sourceNode || !targetNode) {
            continue;
        }

        // Find the ports
        const sourcePort = sourceNode.outPorts.find(port => port.name === connection.sendingPortName);
        const targetPort = targetNode.inPorts.find(port => port.name === connection.receivingPortName);

        // Skip if ports don't exist (this would be caught by ConnectionIntegrityRule)
        if (!sourcePort || !targetPort) {
            continue;
        }

        // Check type compatibility
        const compatibility = checkTypeCompatibility(sourcePort.type, targetPort.type);

        if (!compatibility.compatible) {
            const location =
                context === 'main'
                    ? {
                          context: 'main graph',
                          connectionId: connection.id,
                          sourceNodeId: connection.sendingNodeInstanceId,
                          targetNodeId: connection.receivingNodeInstanceId,
                          sourcePort: connection.sendingPortName,
                          targetPort: connection.receivingPortName,
                      }
                    : {
                          functionId: context,
                          connectionId: connection.id,
                          sourceNodeId: connection.sendingNodeInstanceId,
                          targetNodeId: connection.receivingNodeInstanceId,
                          sourcePort: connection.sendingPortName,
                          targetPort: connection.receivingPortName,
                      };

            errors.push({
                code: 'TYPE_MISMATCH',
                message: `Type mismatch in connection: ${compatibility.reason}`,
                severity: 'error',
                location,
            });
        }
    }

    return errors;
}

interface CompatibilityResult {
    compatible: boolean;
    reason: string;
}

function checkTypeCompatibility(sourceType: DataType, targetType: DataType): CompatibilityResult {
    const unwrappedSourceType = unwrapAllResolvedGenerics(sourceType);
    const unwrappedTargetType = unwrapAllResolvedGenerics(targetType);

    const compatible = typeAMatchesTypeB(unwrappedSourceType, unwrappedTargetType);

    if (compatible) {
        return {
            compatible: true,
            reason: 'Types are compatible',
        };
    } else {
        const sourceTypeStr = getStringRepresentationFromType(unwrappedSourceType);
        const targetTypeStr = getStringRepresentationFromType(unwrappedTargetType);

        return {
            compatible: false,
            reason: `Cannot connect ${sourceTypeStr} to ${targetTypeStr}`,
        };
    }
}
